import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components/macro";
import { AssigneeField } from "../AssigneeField/AssigneeField";
import { AssigneePopOver } from "./AssigneePopOver";
import type { AssigneeOption, AssigneePopOverProps } from "./AssigneePopOver";

export type AssigneePickerProps = {
  name?: string | null;
  selectedId?: string | null;
  stageName: string;
  options: AssigneeOption[];
  onSelect: (option: AssigneeOption | null) => void | Promise<void>;
  disabled?: boolean;
  disabledTooltip?: string;
  unassignedLabel?: string;
  className?: string;
  popoverProps?: Partial<
    Omit<
      AssigneePopOverProps,
      "stageName" | "options" | "selectedId" | "onSelect" | "onClose"
    >
  >;
};

const POPOVER_WIDTH_PX = 380;
const POPOVER_FALLBACK_HEIGHT_PX = 280;
const VIEWPORT_MARGIN_PX = 8;

const Root = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
`;

const PopOverAnchor = styled.div`
  position: fixed;
  z-index: 900;
`;

/**
 * `AssigneeField` + anchored `AssigneePopOver` with outside-click close.
 * Use when you want the full picker with minimal host wiring.
 */
export function AssigneePicker({
  name,
  selectedId = null,
  stageName,
  options,
  onSelect,
  disabled = false,
  disabledTooltip,
  unassignedLabel,
  className,
  popoverProps,
}: AssigneePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  const updatePosition = useCallback(() => {
    if (!rootRef.current) {
      return;
    }
    const trigger = rootRef.current.getBoundingClientRect();
    const popoverEl = popoverRef.current;
    const popoverHeight = popoverEl?.offsetHeight || POPOVER_FALLBACK_HEIGHT_PX;
    const popoverWidth = popoverEl?.offsetWidth || POPOVER_WIDTH_PX;

    const spaceBelow = window.innerHeight - trigger.bottom - VIEWPORT_MARGIN_PX;
    const spaceAbove = trigger.top - VIEWPORT_MARGIN_PX;
    const openUpward = spaceBelow < popoverHeight && spaceAbove > spaceBelow;

    // Pin directly under (or above) the field — no estimated gap.
    let top = openUpward
      ? trigger.top - popoverHeight - VIEWPORT_MARGIN_PX
      : trigger.bottom + VIEWPORT_MARGIN_PX;

    top = Math.min(
      Math.max(VIEWPORT_MARGIN_PX, top),
      Math.max(
        VIEWPORT_MARGIN_PX,
        window.innerHeight - popoverHeight - VIEWPORT_MARGIN_PX
      )
    );

    const left = Math.min(
      Math.max(trigger.left, VIEWPORT_MARGIN_PX),
      Math.max(
        VIEWPORT_MARGIN_PX,
        window.innerWidth - popoverWidth - VIEWPORT_MARGIN_PX
      )
    );

    setPopoverPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    updatePosition();
  }, [isOpen, options.length, selectedId, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      if (!isSelecting) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [close, isOpen, isSelecting, updatePosition]);

  const handleSelect = async (option: AssigneeOption | null) => {
    if (isSelecting) {
      return;
    }
    setIsSelecting(true);
    close();
    try {
      await onSelect(option);
    } finally {
      setIsSelecting(false);
    }
  };

  // Keep Pressed chrome while the popover is open or the select request is in flight.
  const isActive = isOpen || isSelecting;

  const handleFieldClick = () => {
    if (isSelecting) {
      return;
    }
    if (isOpen) {
      close();
      return;
    }
    setIsOpen(true);
  };

  return (
    <Root ref={rootRef} className={className}>
      <AssigneeField
        name={name}
        isOpen={isActive}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        unassignedLabel={unassignedLabel}
        onClick={handleFieldClick}
      />
      {isOpen &&
        !disabled &&
        typeof document !== "undefined" &&
        createPortal(
          <PopOverAnchor
            ref={popoverRef}
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <AssigneePopOver
              {...popoverProps}
              stageName={stageName}
              options={options}
              selectedId={selectedId}
              unassignedLabel={unassignedLabel}
              onSelect={handleSelect}
              onClose={() => {
                if (!isSelecting) {
                  close();
                }
              }}
            />
          </PopOverAnchor>,
          document.body
        )}
    </Root>
  );
}
